import { AfterViewInit, Component, Inject, Injector, OnInit, Optional, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Observable, of } from "rxjs";
import { CoreGridComponent } from "src/app/shared/grid/core-grid/core-grid.component";
import { ChildColumnConfig, ColumnConfig } from "src/app/shared/grid/core-grid/columnConfig.model";
import { SEG } from "src/app/models/seg.model";
import { SegOrgLevel } from "src/app/models/segOrgLevel.model";
import { SegService } from "src/app/services/seg.service";
import { CommonService } from "src/app/services/common.service";
import { ToastService } from "src/app/services/toast.service";
import { AuthService } from "src/app/services/auth.service";
import { SegDialogComponent } from "src/app/modals/seg-dialog/seg-dialog.component";
import nameOf from "src/app/shared/form/nameOf.helper";
import { segsRouteConfig } from "src/app/shared/constants/segsRouteConfig.constants";
import { ARCHIVED_RECORDS_EXPORT_DISABLED_REASON } from "src/app/shared/constants/archivedRecords.constants";
import { RolePermissionObjects } from "src/app/shared/constants/rolePermissionObjects.constants";
import { IFormDialogData } from "src/app/shared/form/form.component";
import { ISegSiteDialogData, SegSiteDialogComponent } from '../../modals/seg-site-dialog/seg-site-dialog.component';
import { ISegOrgLevelDialogData, SegOrgLevelDialogComponent } from '../../modals/seg-org-level-dialog/seg-org-level-dialog.component';
import { SegOrgLevelService } from "src/app/services/seg-org-level.service";
import { DeleteDialog, IDeleteDialogData } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { GridViewService } from "src/app/services/grid-view.service";
import { TranslateService } from "@ngx-translate/core";
import { GridSettingsService } from "src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { ISearchFieldData } from "src/app/shared/grid/baseGrid.component";
import { SegDetailsPopupComponent } from "src/app/common-components/seg-details-popup/seg-details-popup.component";
import { FlyoutContainerComponent } from "src/app/shared/flyout/flyout-container/flyout-container.component";
import { AdditionalButton, BatchAction } from "src/app/models/batchAction.model";
import { ISegDeleteMergeDialogData, SegDeleteMergeDialogComponent } from "src/app/modals/seg-delete-merge-dialog/seg-delete-merge-dialog.component";
import { SegDeleteMergeResult } from "src/app/modals/seg-delete-merge-dialog/seg-delete-merge-dialog.model";
import { GridView } from "src/app/models/gridView.model";
import { hasSegOrgLevelPermission } from "src/app/_helpers/seg-org-level-permission.helper";
import { GridPermissions } from "src/app/shared/grid/core-grid/gridPermissions.enum";

@Component({
  standalone: false,
  selector: 'app-seg-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', 'seg-list-component.scss']
})
export class SegListComponent extends CoreGridComponent<SEG> implements OnInit, AfterViewInit {
  @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;
  private showArchivedOnly = false;
  private readonly addedToMySiteFilterKey = nameOf<SEG>(x => x.addedToMySite);
  // DASH-3000: Status was dropped from the grid; strip it from any saved GridView so old views still load.
  private readonly removedStatusColumnProperty = 'isActive';

  constructor(
    private segService: SegService,
    toastService: ToastService,
    dialog: MatDialog,
    commonService: CommonService,
    authService: AuthService,
    private segOrgLevelService: SegOrgLevelService,
    gridViewService: GridViewService,
    private translateService: TranslateService,
    gridSettingsService: GridSettingsService,
    private organizationLevelService: OrganizationLevelService,
    protected injector: Injector,
    private route: ActivatedRoute,
    @Optional() @Inject(MAT_DIALOG_DATA) public searchFieldData: ISearchFieldData
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    this.gridService = segService;
  }

  protected get objectId(): string {
    return RolePermissionObjects.SEG;
  }

  // Add To My Site funnels through BulkAddSegOrgLevels, which the backend authorizes as a
  // genuine Create against GLOBAL_Seg_OrgLevel — so gate it on Create (of that object, or the
  // GLOBAL_SEG fallback), not the old Update proxy. Widget mode still suppresses edit affordances.
  protected get canAddToMySite(): boolean {
    return this.canCreate || (!this.widgetMode && hasSegOrgLevelPermission(this.permissionService, GridPermissions.Create));
  }

  // A removal is authorized against GLOBAL_Seg_OrgLevel Delete on its own flag
  // (PermissionCheckInterceptor runs before SoftDeleteIntercept), so gate the grid
  // Remove separately from the Create/Update entry points.
  protected get canRemoveOrgLevel(): boolean {
    return this.canDelete || (!this.widgetMode && hasSegOrgLevelPermission(this.permissionService, GridPermissions.Delete));
  }

  protected override get isGridViewEnabled(): boolean { return true; }

  protected override get enableCheckboxSelection(): boolean { return true; }

  get hasOrgFilter(): boolean {
    return true;
  }

  ngOnInit(): void {
    const searchDialogRef = this.injector.get<MatDialogRef<SegListComponent> | null>(MatDialogRef, null);
    if (searchDialogRef) {
      this.searchDialogRef = searchDialogRef;
    }
    super.ngOnInit();
    // Rebuild the Bulk Edit entry points now that role permissions have been
    // applied (applyRolePermission runs after initializeProperties).
    this.setBulkEditControls();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
    this.applySearchQueryParam();
  }

  private applySearchQueryParam(): void {
    const search = this.route.snapshot.queryParams.search?.trim();
    if (!search || !this.searchField?.nativeElement) {
      return;
    }

    this.searchField.nativeElement.value = search;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadPage();
  }

  protected override async onGridViewInitialized(view: GridView | null): Promise<void> {
    if (view?.settingsJson?.columns) {
      view = {
        ...view,
        settingsJson: {
          ...view.settingsJson,
          columns: view.settingsJson.columns.filter(col => col.property !== this.removedStatusColumnProperty),
        },
      };
    }
    await super.onGridViewInitialized(view);
  }

  // DASH-3187: the row-level "Add To My Site" action (and the single-linked-site
  // edit) opens the single-org-level form, NOT the bulk scope-matrix stepper.
  // The bulk stepper (SegSiteDialogComponent) is reachable only from the bulk /
  // multi-select entry points via openBulkEditDialog().
  openSegSiteDialog(id: string, seg: SEG, organizationLevelId?: string) {
    const data: ISegOrgLevelDialogData = {
      id: id,
      segId: seg.id,
      organizationLevelId: organizationLevelId ?? this.selectedOrg?.id
    }
    this.openDialog(SegOrgLevelDialogComponent, data);
  }

  openBulkEditDialog(): void {
    // Two entry points funnel through here: the toolbar Bulk Edit button (may be
    // empty) and the multi-select batch action. Step 1 scopes to whatever
    // selection it was entered with — an empty selection intentionally opens with
    // no SEGs pre-selected rather than defaulting to every SEG at the site.
    const selected = this.selectionService?.selectedItems ?? [];
    const segIds = selected.map(seg => seg.id);

    const data: ISegSiteDialogData = {
      segIds,
      organizationLevelId: this.selectedOrg?.id
    };

    const dialogRef = this.dialog.open(SegSiteDialogComponent, {
      data,
      width: '1360px',
      disableClose: true,
      panelClass: 'seg-site-dialog-panel',
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.clearRowSelection();
      if (this.isSavedDialogResult(result)) {
        this.loadPage();
      }
    });
  }

  openRemoveFromSiteDialog(id: string) {
    const data: IDeleteDialogData = {
      id: id,
      formDeletes: true,
      customDelete: () => this.segOrgLevelService.deleteById(id, this.selectedOrg.id),
      title: this.translateService.instant('SEG from Site'),
      service: this.segOrgLevelService,
    }
    const dialogRef = this.dialog.open(DeleteDialog, {
      data: data,
      width: '440px',
      disableClose: true
    });
    this.handleDialogClose(dialogRef);
  }

  override initializeColumns(): void {
    this.columnConfigs = [
      new ColumnConfig<SEG>({
        name: nameOf<SEG>(x => x.name),
        displayName: 'SEG Name',
        cellTemplate: (seg: SEG) => seg.name,
        onClick: (seg: SEG) => {
          if (!this.showArchivedOnly) {
            this.showSEGDetails(seg);
          }
        },
      }),
      new ColumnConfig<SEG>({
        name: nameOf<SEG>(x => x.code),
        displayName: 'SEG Code',
        cellTemplate: (seg: SEG) => seg.code ?? '-',
      }),
      new ColumnConfig<SEG>({
        name: nameOf<SEG>(x => x.description),
        displayName: 'Description',
        cellTemplate: (seg: SEG) => seg.description ?? '-',
      }),
      new ColumnConfig<SEG>({
        name: nameOf<SEG>(x => x.organizationLevelId),
        displayName: 'Visibility',
        isSortable: false,
        cellTemplate: (seg: SEG) => seg.organizationLevelName,
      }),
      new ColumnConfig<SEG>({
        name: nameOf<SEG>(x => x.archiveType),
        displayName: 'Archive Type',
        cssClass: () => this.showArchivedOnly ? '' : 'd-none',
        headerCssClass: () => this.showArchivedOnly ? '' : 'd-none',
        cellTemplate: (seg: SEG) => seg.archiveType ? this.translateService.instant(seg.archiveType) : '-',
      }),
      new ColumnConfig<SEG>({
        name: nameOf<SEG>(x => x.mergedIntoName),
        displayName: 'Merged Into',
        cssClass: () => this.showArchivedOnly ? '' : 'd-none',
        headerCssClass: () => this.showArchivedOnly ? '' : 'd-none',
        cellTemplate: (seg: SEG) => seg.archiveType === 'Merged' ? (seg.mergedIntoName ?? '-') : '-',
      }),
      new ColumnConfig<SEG>({
        name: nameOf<SEG>(x => x.addedToMySite),
        displayName: 'Site Assignment',
        isDisplayNameConfigurable: false,
        isSortable: false,
        cssClass: () => this.showArchivedOnly ? 'd-none' : '',
        headerCssClass: () => this.showArchivedOnly ? 'd-none' : '',
        cellTemplate: (seg: SEG) => '',
        children: (row: SEG) => {
          const siteCountChild = this.buildSiteCountChild(row);

          if (!row?.addedToMySite) {
            if (!this.canAddToMySite) {
              return [];
            }

            return [
              new ChildColumnConfig<SEG>({
                onClick: (seg: SEG) => this.openSegSiteDialog('', seg),
                cssClass: () => 'seg-btn main-btn',
                cellTemplate: () => this.translateService.instant('Add To My Site')
              }),
            ]
          }
          else if (this.getAddedSiteCount(row) === 1 && row.addedAtSelectedOrgLevel) {
            return this.buildAddedAtSelectedOrgLevelChildren();
          }
          else {
            return [siteCountChild];
          }
        }
      }),
      { name: 'action', displayName: 'Action' }
    ];
  }

  private buildSiteCountChild(seg: SEG): ChildColumnConfig<SEG> {
    const siteCount = this.getAddedSiteCount(seg);

    return new ChildColumnConfig<SEG>({
      cssClass: () => `seg-site-count-btn main-btn${siteCount > 0 ? ' is-clickable' : ''}`,
      cellTemplate: () => `${siteCount}`,
      onClick: siteCount > 0 ? (row: SEG) => this.openLinkedSites(row) : undefined,
      readonly: () => siteCount === 0,
    });
  }

  // State B: a SEG associated at exactly the selected org level (and nowhere else in
  // scope) shows a non-interactive [Added] pill plus a [Remove] button. Remove targets
  // the selected org level via openRemoveFromSiteDialog and only appears with Delete.
  private buildAddedAtSelectedOrgLevelChildren(): ChildColumnConfig<SEG>[] {
    const children: ChildColumnConfig<SEG>[] = [
      new ChildColumnConfig<SEG>({
        cssClass: () => 'seg-btn',
        cellTemplate: () => this.translateService.instant('Added'),
        readonly: () => true,
      }),
    ];

    if (this.canRemoveOrgLevel) {
      children.push(
        new ChildColumnConfig<SEG>({
          onClick: (seg: SEG) => this.openRemoveFromSiteDialog(seg.id),
          cssClass: () => 'seg-remove-btn',
          cellTemplate: () => this.translateService.instant('Remove'),
        }),
      );
    }

    return children;
  }

  private openLinkedSites(seg: SEG): void {
    const siteCount = this.getAddedSiteCount(seg);
    if (siteCount <= 0) {
      return;
    }

    if (siteCount > 1) {
      this.showSEGDetails(seg);
      return;
    }

    this.openSingleLinkedSite(seg);
  }

  private openSingleLinkedSite(seg: SEG): void {
    this.segOrgLevelService.getAssociations(seg.id).subscribe({
      next: (associations) => {
        const linkedSite = associations.find((item) => !!item.organizationLevelId);
        if (!linkedSite?.organizationLevelId) {
          this.showSEGDetails(seg);
          return;
        }

        this.openSegSiteDialog(seg.id, seg, linkedSite.organizationLevelId);
      },
      error: (error) => {
        this.toastService.showError(error);
      },
    });
  }

  private getAddedSiteCount(seg: SEG): number {
    return seg.siteCount ?? (seg.addedToMySite ? 1 : 0);
  }

  protected objectFilterMap(propertyName: string) {
    switch (propertyName) {
      case nameOf<SEG>(x => x.organizationLevelId): return this.organizationLevelService;
      default: return undefined;
    }
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override initializeTabs() {
    return segsRouteConfig;
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'SEG';
    this.searchPlaceholder = 'Search SEGs';
    this.selectedItemsLabel = 'SEG';
    this.deleteActionLabel = 'Archive';
    this.deleteActionIcon = 'archive';
    this.setBulkEditControls();
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  showSEGDetails(seg: SEG): void {
    this.openFlyoutInternal(SegDetailsPopupComponent, seg.id, '80vw', true);
  }

  override openEntityDialog(entityId?: string): void {
    this.openSegDialog(entityId || '');
  }

  override openEntityDeleteDialog(entity: SEG): void {
    if (this.showArchivedOnly) {
      this.isLoading = true;
      this.segService.restore(entity.id).subscribe({
        next: () => {
          this.toastService.showSuccess('SEG restored successfully', true);
          this.loadPage();
        },
        error: (error) => {
          this.toastService.showError(error);
          this.isLoading = false;
        }
      });

      return;
    }

    this.openSegDeleteMergeDialog(entity);
  }

  private openSegDeleteMergeDialog(entity: SEG): void {
    const data: ISegDeleteMergeDialogData = {
      segId: entity.id,
      segName: entity.name,
      organizationLevelId: entity.organizationLevelId,
    };

    const dialogRef = this.dialog.open(SegDeleteMergeDialogComponent, {
      data,
      width: '480px',
      disableClose: true,
      panelClass: 'seg-delete-merge-dialog-panel',
    });
    this.handleDialogClose(dialogRef, (result) =>
      result === SegDeleteMergeResult.Deleted ? this.removeRow(entity.id) : this.loadPage());
  }

  override loadPage(): void {
    if (!this.showArchivedOnly) {
      super.loadPage();
      return;
    }

    this.isLoading = true;
    this.segService.getDeletedList(
      this.searchField?.nativeElement?.value ?? '',
      this.sortActive || this.getDefaultOrderByColumn(),
      this.defaultOrderBy || 'asc',
      this.paginator?.pageIndex ?? 0,
      this.paginator?.pageSize ?? this.getPageSize(),
    ).subscribe({
      next: (result) => {
        this.dataSource.updateData(result.data);
        if (this.paginator) {
          this.paginator.length = result.count;
        }
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  // Bulk Edit is the primary entry point and respects the same
  // RolePermissionObjects.SEG / Update permission the flyout Edit button uses.
  // Show Archived SEGs is demoted to a secondary outline below it until the
  // CoreGrid filterRowToggles work lands (tracked separately).
  // DASH-3213: Bulk Edit's Add to Site step is unusably slow on tenants with a
  // large org level hierarchy (GetAssociations taking 25s+). Hiding both entry
  // points (toolbar button + batch action) until the perf fix ships.
  private setBulkEditControls(): void {
    const canBulkEdit = false;

    this.batchActions = canBulkEdit
      ? ([
        {
          label: 'Bulk Edit',
          matIcon: 'edit',
          disabled: true,
          onClick: () => this.openBulkEditDialog()
        }
      ] as BatchAction[])
      : [];

    const buttons: AdditionalButton[] = [];
    if (canBulkEdit) {
      buttons.push({
        title: 'Bulk Edit',
        readonly: false,
        class: 'seg-bulk-edit-btn',
        onClick: () => this.openBulkEditDialog()
      });
    }
    buttons.push({
      title: this.showArchivedOnly ? 'Show Active SEGs' : 'Show Archived SEGs',
      readonly: false,
      class: 'seg-additional-btn-secondary',
      onClick: () => this.toggleArchivedView()
    });
    this.additionalButtons = buttons;
  }

  openSegDialog(segId: string = ''): void {
    const segModel: IFormDialogData = {
      id: segId
    };

    this.openDialog(SegDialogComponent, segModel);
  }

  private toggleArchivedView(): void {
    this.showArchivedOnly = !this.showArchivedOnly;
    if (this.showArchivedOnly) {
      const idx = this.currentFilters.findIndex(f => f.column.property === this.addedToMySiteFilterKey);
      if (idx > -1) {
        this.currentFilters.splice(idx, 1);
        this.activeGridFilters[this.addedToMySiteFilterKey] = false;
        if (this.selectedGridView?.settingsJson?.filters) {
          delete this.selectedGridView.settingsJson.filters[this.addedToMySiteFilterKey];
        }
      }
    }

    this.editDisabled = this.showArchivedOnly;
    this.deleteDisabled = this.showArchivedOnly;
    this.exportDisabledReason = this.showArchivedOnly ? ARCHIVED_RECORDS_EXPORT_DISABLED_REASON : null;
    this.headerClassByColumn[nameOf<SEG>(x => x.addedToMySite)] = this.showArchivedOnly ? 'd-none' : '';    
    this.additionalActions = this.showArchivedOnly ? [
      {
        name: 'Restore',
        icon: 'fas fa-undo',
        action: (row: SEG) => this.openEntityDeleteDialog(row)
      }
    ] : [];
    
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.clearRowSelection();
    this.setBulkEditControls();
    this.loadPage();
  }

  protected override onSelectionChanged(): void {
    const count = this.selectionService?.selectedItems.length ?? 0;
    this.selectedRowsCount = count;

    const bulkEditAction = this.batchActions.find(a => a.label === 'Bulk Edit');
    if (bulkEditAction) {
      bulkEditAction.disabled = count === 0;
    }
  }
}
